import { ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate } from "@/lib/router-compat";
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useMembership } from '@/hooks/useMembership';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Loader2 } from 'lucide-react';
import { hasConciergeIntent, hasConciergeLocalDraft } from '@/lib/conciergeRouting';
import {
  deriveAccountLifecycleState,
  LIFECYCLE_REDIRECT,
  type AccountLifecycleState,
} from '@/lib/accountLifecycle';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Global route wrapper. The gating order lives in ONE place —
 * `deriveAccountLifecycleState` (and its database twin
 * `public.account_lifecycle_state`) — so every account resolves to exactly one
 * lifecycle state and one destination. No state can redirect into another
 * state's route.
 *
 * Stage 5 decision: `core_onboarding_completed` is NOT enforced. The single
 * onboarding gate stays `onboarding_completed`.
 *
 * Singles flow protection: no mode-aware gates here — the singles default
 * resolves to the same path it always did.
 *
 * Includes a 4s safety timeout to prevent infinite loading.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading, isEmailVerified } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile();
  const { currentTier, hasComplimentaryAccess, membership } = useMembership();
  const { track } = useAnalytics();
  const [timedOut, setTimedOut] = useState(false);
  const lastTracked = useRef<AccountLifecycleState | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), 4000);
    return () => window.clearTimeout(timer);
  }, []);

  const isStillLoading = (authLoading || (user && profileLoading)) && !timedOut;

  const lifecycleState = deriveAccountLifecycleState(profile, isEmailVerified);

  // Funnel telemetry: state label only. No email, name, id or any profile value.
  useEffect(() => {
    if (!user || isStillLoading || lifecycleState === 'unknown') return;
    if (lastTracked.current === lifecycleState) return;
    lastTracked.current = lifecycleState;
    void track({
      event_name: 'account_lifecycle_state',
      event_category: 'funnel',
      event_data: { lifecycle_state: lifecycleState },
    });
  }, [user, isStillLoading, lifecycleState, track]);

  if (isStillLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Auth is authoritative for email verification, even before the profile loads.
  if (!isEmailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Plan selection is bypassed for admin-granted tiers, complimentary access and
  // concierge intent — unchanged from before.
  if (lifecycleState === 'pending_plan') {
    const hasAdminAccess =
      currentTier === 'elite' ||
      currentTier === 'premium' ||
      currentTier === 'plus' ||
      hasComplimentaryAccess ||
      membership.isAdminOverride;

    if (hasAdminAccess) {
      return <>{children}</>;
    }
    if (hasConciergeIntent(profile) || hasConciergeLocalDraft()) {
      return <Navigate to="/concierge-submitted" replace />;
    }
    return <Navigate to="/choose-plan" replace />;
  }

  const redirect = LIFECYCLE_REDIRECT[lifecycleState];
  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  return <>{children}</>;
}
